import React, { useState, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Table, TableContainer, TablePagination, Paper, makeStyles } from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';

import Alert from '../../../../shared/Alert';

import { GtinHeadTable } from './gtin-head-table';
import { GtinBodyTable } from './gtin-body-table';
import { GtinToolbarTable } from './gtin-toolbar-table';

import { deleteGtinReference } from '../../../../../actions/gtinActions';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
    cursor: 'pointer',
  },
  container: {
    height: '100%',
    maxHeight: window.innerHeight - 260,
  },
  tabPaginationPaper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    border: 'none',
    boxShadow: 'none',
    paddingRight: 20,
    minWidth: 200,
  },
  paginationPaper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItem: 'center',
  },
  tabPagination: {
    display: 'flex',
    alignItems: 'flex-end',
    padding: 10,
  },
  checked: {
    color: theme.palette.primary.main,
    '&$checked': {
      color: theme.palette.primary.main,
    },
  },
}));

const GtinTable = (props) => {
  const classes = useStyles();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('calories');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [error, setError] = useState(false);
  const [errorType, setErrorType] = useState('success');
  const [errorText, setErrorText] = useState('');

  const { rows, setModalEditMode, setEditCurrentGtin, openDrawer, tenantData, deleteGtinReference, setIsLoading } =
    props;

  const editGtin = (id) => {
    setModalEditMode(true);
    setEditCurrentGtin(id);
    openDrawer();
  };

  const removeGtinReference = async (row) => {
    setIsLoading(true);
    try {
      await deleteGtinReference(tenantData.docId, row.id);
      setErrorText(`${row.gtin} was successfully removed`);
      setError(true);
    } catch {
      setErrorText('Something went wrong');
      setErrorType('error');
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const removeSelectedReference = async () => {
    let generatedResponse = [];
    await Promise.all(
      selected.map(async (ids) => {
        setIsLoading(true);
        try {
          let insertResponse = await deleteGtinReference(tenantData.docId, ids);
          generatedResponse.push(insertResponse);
          setErrorText(`${selected.length} GTIN was successfully removed`);
          setError(true);
        } catch (error) {
          setErrorText('Something went wrong');
          setErrorType('error');
          setError(true);
        } finally {
          setSelected([]);
        }
      }),
    );
    setIsLoading(false);
    return generatedResponse;
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (event, row) => {
    event.stopPropagation();
    editGtin({ ...row });
  };

  const handleRowSelect = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }

    setSelected(newSelected);
  };

  const handleChangePageTab = (event, newPage) => {
    setPage(newPage - 1);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  return (
    <Fragment>
      <div id="locations-table" className={classes.root}>
        <Paper className={classes.paper}>
          {selected.length > 0 && (
            <GtinToolbarTable numSelected={selected.length} removeSelectedReference={removeSelectedReference} />
          )}
          <TableContainer className={classes.container}>
            <Table
              stickyHeader
              className={classes.table}
              aria-labelledby="tableTitle"
              size="medium"
              aria-label="enhanced table"
            >
              <GtinHeadTable
                classes={classes}
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={rows.length}
              />
              <GtinBodyTable
                classes={classes}
                rows={rows}
                page={page}
                order={order}
                orderBy={orderBy}
                rowsPerPage={rowsPerPage}
                isSelected={isSelected}
                handleRowClick={handleRowClick}
                handleRowSelect={handleRowSelect}
                editGtin={editGtin}
                removeGtinReference={removeGtinReference}
              />
            </Table>
          </TableContainer>
          <Paper className={classes.paginationPaper}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 75, 100]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              labelRowsPerPage="Results"
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
            <Paper className={classes.tabPaginationPaper}>
              <div
                style={{
                  fontWeight: 'bold',
                  color: page === 0 ? '#dcdfe4' : tenantData.mainColor,
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  if (page !== 0) {
                    setPage(page - 1);
                  }
                }}
              >
                Prev
              </div>
              <Pagination
                className={classes.tabPagination}
                count={Math.ceil(rows.length / rowsPerPage)}
                page={page + 1}
                hideNextButton
                hidePrevButton
                onChange={handleChangePageTab}
                defaultPage={1}
                color="primary"
              />
              <div
                style={{
                  fontWeight: 'bold',
                  color: page < Math.ceil(rows.length / rowsPerPage) - 1 ? tenantData.mainColor : '#dcdfe4',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  if (page < Math.ceil(rows.length / rowsPerPage) - 1) {
                    setPage(page + 1);
                  }
                }}
              >
                Next
              </div>
            </Paper>
          </Paper>
        </Paper>
      </div>

      <Alert variant={errorType} message={errorText} open={error} onClose={() => setError(false)} />
    </Fragment>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      deleteGtinReference,
    },
    dispatch,
  );

const mapStateToProps = (state) => ({
  tenantData: state.tenant,
});

export default connect(mapStateToProps, mapDispatchToProps)(GtinTable);
